import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Button, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, Alert, Stack, Tooltip, CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import api from "../services/api";

function todayStr() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function PendingIntakes() {
  const [list, setList]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDetail, setOpenDetail] = useState(null); // intake id when modal is open
  const [detail, setDetail] = useState({ intake: null, lines: [] });
  const [rates, setRates]   = useState({});           // { Detail_ID: "5.69" }
  const [marketByCanonical, setMarketByCanonical] = useState({}); // { canonical: { CNY: 5.79, ... } }
  const [saving, setSaving] = useState(false);
  const [toast, setToast]   = useState({ open: false, message: "", severity: "info" });

  const showToast  = (message, severity = "info") => setToast({ open: true, message, severity });
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/intakes", { params: { status: "DRAFT" } });
      setList(r.data?.intakes || []);
    } catch (err) {
      showToast(`Failed to load: ${err.response?.data?.message || err.message}`, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openIntake = async (id) => {
    setOpenDetail(id);
    setDetail({ intake: null, lines: [] });
    setRates({});
    setMarketByCanonical({});
    try {
      const r = await api.get(`/intakes/${id}`);
      setDetail(r.data);
      const initialRates = {};
      for (const l of r.data.lines || []) initialRates[l.Detail_ID] = "";
      setRates(initialRates);
    } catch (err) {
      showToast(`Failed to open: ${err.response?.data?.message || err.message}`, "error");
    }
  };

  const closeIntake = () => { setOpenDetail(null); setDetail({ intake: null, lines: [] }); };

  const setRate = (detailId, value) => setRates((r) => ({ ...r, [detailId]: value }));

  // ---- Market suggestion: pull today's comparison and index by canonical ----
  const fetchMarketSuggestion = async () => {
    try {
      const r = await api.get("/prices/comparison", { params: { date: detail.intake?.IntakeDate?.slice(0, 10) || todayStr() } });
      const map = {};
      for (const row of r.data?.rows || []) {
        map[row.canonical.toUpperCase()] = row.prices || {};
      }
      setMarketByCanonical(map);
    } catch (err) {
      showToast("No market data available for that date", "info");
    }
  };

  // Crude canonical-name lookup from raw item description
  // (mirrors the backend's case-insensitive canonical name; falls back to
  // category-level matching if exact lookup misses).
  function suggestForDescription(description) {
    if (!description) return null;
    const upper = description.toUpperCase();
    if (marketByCanonical[upper]) return marketByCanonical[upper];
    // fallback: prefix match
    for (const k of Object.keys(marketByCanonical)) {
      if (upper.includes(k) || k.includes(upper)) return marketByCanonical[k];
    }
    return null;
  }

  const totals = useMemo(() => {
    let netSum = 0;
    let amtSum = 0;
    for (const l of detail.lines || []) {
      const net = Number(l.N_Wt) || 0;
      const rate = Number(rates[l.Detail_ID]) || 0;
      netSum += net;
      amtSum += +(net * rate).toFixed(2);
    }
    return { netSum, amtSum };
  }, [detail.lines, rates]);

  const handleConvert = async () => {
    if (!detail.intake) return;
    const missing = (detail.lines || []).filter((l) => rates[l.Detail_ID] === "" || rates[l.Detail_ID] == null);
    if (missing.length) return showToast(`Enter price for all ${detail.lines.length} lines`, "warning");

    setSaving(true);
    try {
      const payload = {
        lines: (detail.lines || []).map((l) => ({
          Detail_ID: l.Detail_ID,
          Rate:      Number(rates[l.Detail_ID]),
        })),
      };
      const r = await api.post(`/intakes/${detail.intake.Intake_ID}/convert`, payload);
      showToast(`Converted to invoice ${r.data?.Master_ID}`, "success");
      closeIntake();
      loadList();
    } catch (err) {
      showToast(`Convert failed: ${err.response?.data?.message || err.message}`, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleVoid = async () => {
    if (!detail.intake) return;
    const reason = window.prompt("Reason for voiding this intake (optional):") || "";
    setSaving(true);
    try {
      await api.post(`/intakes/${detail.intake.Intake_ID}/void`, { reason });
      showToast("Intake voided", "success");
      closeIntake();
      loadList();
    } catch (err) {
      showToast(`Void failed: ${err.response?.data?.message || err.message}`, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6">Pending Intakes</Typography>
        <Chip label={list.length} size="small" color={list.length > 0 ? "warning" : "default"} />
        <IconButton size="small" onClick={loadList} disabled={loading}>
          <RefreshIcon fontSize="small" />
        </IconButton>
        {loading && <CircularProgress size={16} />}
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Intake #</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Customer</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Employee</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>Lines</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>Total Net (lb)</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && list.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ color: "text.secondary", py: 4 }}>No pending intakes — all caught up.</TableCell></TableRow>
            )}
            {list.map((r) => (
              <TableRow key={r.Intake_ID} hover>
                <TableCell sx={{ fontFamily: "monospace" }}>{r.IntakeNumber}</TableCell>
                <TableCell>{r.IntakeDate?.slice(0, 10)}</TableCell>
                <TableCell>{r.Customer_Name}</TableCell>
                <TableCell>{r.CreatedBy_Username || "—"}</TableCell>
                <TableCell align="right">{r.LineCount}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                  {r.TotalNetWt != null ? Number(r.TotalNetWt).toFixed(2) : "—"}
                </TableCell>
                <TableCell>
                  <Tooltip title="Open & price">
                    <IconButton size="small" onClick={() => openIntake(r.Intake_ID)}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail / Price modal */}
      <Dialog open={!!openDetail} onClose={closeIntake} maxWidth="md" fullWidth>
        {detail.intake && (
          <>
            <DialogTitle>
              {detail.intake.IntakeNumber} — {detail.intake.Customer_Name}
              <Typography variant="body2" color="text.secondary">
                {detail.intake.IntakeDate?.slice(0, 10)} · by {detail.intake.CreatedBy_Username || "unknown"}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                <Button size="small" startIcon={<LightbulbOutlinedIcon />} onClick={fetchMarketSuggestion}>
                  Suggest from market
                </Button>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Material</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>Net (lb)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>Rate $/lb</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>Line $</TableCell>
                      <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>Market today</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.lines || []).map((l) => {
                      const net  = Number(l.N_Wt) || 0;
                      const rate = Number(rates[l.Detail_ID]) || 0;
                      const amt  = +(net * rate).toFixed(2);
                      const sug  = suggestForDescription(l.Item_Description);
                      return (
                        <TableRow key={l.Detail_ID}>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {l.PhotoFile && (
                                <Box
                                  component="img"
                                  src={`/uploads/${l.PhotoFile}`}
                                  alt=""
                                  onClick={() => window.open(`/uploads/${l.PhotoFile}`, "_blank")}
                                  sx={{ width: 36, height: 36, borderRadius: 1, objectFit: "cover", cursor: "pointer", border: "1px solid #ddd" }}
                                />
                              )}
                              <span>{l.Item_Description}</span>
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: "monospace" }}>{net.toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <TextField
                              value={rates[l.Detail_ID] ?? ""}
                              onChange={(e) => setRate(l.Detail_ID, e.target.value)}
                              size="small"
                              type="number"
                              inputProps={{ inputMode: "decimal", step: 0.01, min: 0, style: { textAlign: "right" } }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: "bold" }}>
                            ${amt.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {sug ? (
                              <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
                                {Object.entries(sug).map(([vendor, price]) => (
                                  <Chip
                                    key={vendor}
                                    size="small"
                                    label={`${vendor}: $${Number(price).toFixed(2)}`}
                                    onClick={() => setRate(l.Detail_ID, String(price))}
                                    sx={{ cursor: "pointer" }}
                                  />
                                ))}
                              </Stack>
                            ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Totals</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: "bold" }}>
                        {totals.netSum.toFixed(2)}
                      </TableCell>
                      <TableCell />
                      <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: "bold", fontSize: 16 }}>
                        ${totals.amtSum.toFixed(2)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {detail.intake.Notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">Notes from employee:</Typography>
                  <Typography variant="body2">{detail.intake.Notes}</Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button color="error" onClick={handleVoid} disabled={saving}>Void</Button>
              <Box sx={{ flex: 1 }} />
              <Button onClick={closeIntake} disabled={saving}>Cancel</Button>
              <Button variant="contained" onClick={handleConvert} disabled={saving}>
                {saving ? "Converting…" : "Create Purchase Invoice"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={closeToast} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={closeToast} variant="filled">{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
}
